"use client";

import { useSearchParams } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { useEffect, useMemo } from "react";
import { useTranslations } from "next-intl";
import { toast } from "@/lib/notify";
import { useAuthProviders } from "@/lib/useAuthProviders";
import { setSignupRoleHint } from "@/lib/signupRoleHint";
import { useSession } from "@/lib/auth-react";
import { LanguageSwitcher } from "@/components/i18n/LanguageSwitcher";

/**
 * Clamp a user-supplied callback URL to a same-origin path so we can't
 * be open-redirected to evil.example.com. Matches the backend's
 * `safeCallbackPath` in `auth-oauth-handshake`.
 */
function sanitizeCallbackUrl(raw: string | null | undefined): string {
  const fallback = "/dashboard";
  if (!raw || typeof raw !== "string") return fallback;
  // Reject absolute URLs (`http://…`, `https://…`, `javascript:…`).
  if (/^[a-z][a-z0-9+\-.]*:/i.test(raw)) return fallback;
  // Reject protocol-relative URLs (`//evil.com/path`).
  if (raw.startsWith("//")) return fallback;
  // Must be a server path on the frontend.
  if (!raw.startsWith("/")) return fallback;
  // Never bounce back to /auth itself — would loop.
  if (raw === "/auth" || raw.startsWith("/auth?") || raw.startsWith("/auth/"))
    return fallback;
  return raw;
}

// Known OAuth error codes. The user-facing copy is resolved from the
// "Auth" message namespace at render time (errorTitle.<code> /
// errorBody.<code>), falling back to the Default variant.
const AUTH_ERROR_CODES = [
  "OAuthCallback",
  "UnsupportedProvider",
  "ProviderDisabled",
  "ACCOUNT_BLOCKED",
  "ACCOUNT_DISABLED",
] as const;

/**
 * Unified sign-in / sign-up landing.
 *
 * All sign-ins go through the backend OAuth handshake. Role context comes
 * from `?role=`, set by the marketing CTAs on `/teach` and `/learn` and
 * by legacy redirects.
 *
 * Role disambiguation is handled *after* OAuth: a sign-up that lands
 * here without a role hint is created with `roleConfirmedAt = null` and
 * routed through `/auth/choose-role` by middleware on first dashboard hit.
 */
export default function AuthPage() {
  const t = useTranslations("Auth");
  const searchParams = useSearchParams();
  const { data: session, status } = useSession();
  const sessionExpired = searchParams?.get("expired") === "1";
  const errorParam = searchParams?.get("error");
  const callbackUrl = sanitizeCallbackUrl(searchParams?.get("callbackUrl"));
  const roleParam = searchParams?.get("role");

  // Already authed visitors shouldn't see the sign-in form — bounce them
  // straight to their post-auth destination. Covers the case where a
  // logged-in user hits a `/auth?callbackUrl=/foo` link (or returns to
  // a stale tab) and the bare `/auth` page rendering would be confusing.
  //
  // Before navigating: fire a client-side `/api/auth/refresh` so the
  // browser's cookie jar matches the server-side session. The server
  // session reader (`lib/session.ts:getServerSession`) can refresh
  // tokens in memory but can't write the rotated cookies back to the
  // browser when invoked from a server-component layout — `cookies().set()`
  // throws outside route handlers / server actions. Without this sync,
  // the destination route's middleware sees the OLD (expired) access
  // cookie, fails its own refresh attempt, and bounces back to /auth —
  // creating a hard reload loop. See #739.
  useEffect(() => {
    if (status !== "authenticated" || !session?.user) return;
    let cancelled = false;
    fetch("/api/auth/refresh", {
      method: "POST",
      credentials: "include",
      cache: "no-store",
    })
      .catch(() => {
        // Network errors fall through to the navigation — middleware will
        // re-evaluate cookies on the destination route.
      })
      .finally(() => {
        if (cancelled) return;
        window.location.assign(callbackUrl);
      });
    return () => {
      cancelled = true;
    };
  }, [status, session, callbackUrl]);
  const signupRole: "STUDENT" | "TEACHER" | null = useMemo(() => {
    if (!roleParam) return null;
    const lower = roleParam.toLowerCase();
    if (lower === "teacher") return "TEACHER";
    if (lower === "student") return "STUDENT";
    return null;
  }, [roleParam]);

  const { providers, loading: providersLoading } = useAuthProviders();
  const showGoogle = providers?.google === true;
  const showApple = providers?.apple === true;
  const showMockOauth = providers?.mockOauth === true;
  const noProvidersEnabled =
    providers !== null && !showGoogle && !showApple && !showMockOauth;

  const handleOAuthClick = (provider: "google" | "apple" | "mock") => {
    // Stamp the role-hint cookie before the OAuth round-trip so the
    // backend creates new users with the right role + roleConfirmedAt.
    // No hint → user lands on /auth/choose-role after sign-in.
    if (signupRole) setSignupRoleHint(signupRole);

    const params = new URLSearchParams();
    if (signupRole) params.set("role", signupRole);
    if (callbackUrl) params.set("callbackUrl", callbackUrl);
    window.location.assign(
      `/api/auth/oauth/${provider}/start?${params.toString()}`,
    );
  };

  // Surface session-expired as a toast. OAuth errors are rendered inline
  // as a banner above the providers (see authErrorBanner below).
  useEffect(() => {
    if (!sessionExpired) return;
    const timer = setTimeout(() => {
      toast.error(t("sessionExpiredToast"), {
        duration: 5000,
      });
    }, 100);
    return () => clearTimeout(timer);
  }, [sessionExpired, t]);

  const errorCode =
    errorParam &&
    (AUTH_ERROR_CODES as readonly string[]).includes(errorParam)
      ? errorParam
      : "Default";
  const errorCopy = errorParam
    ? {
        title: t(`errorTitle.${errorCode}`),
        body: t(`errorBody.${errorCode}`),
      }
    : null;

  const heading = signupRole
    ? signupRole === "TEACHER"
      ? t("headingTeacherSignUp")
      : t("headingStudentSignUp")
    : t("headingSignIn");

  const subheading = signupRole
    ? signupRole === "TEACHER"
      ? t("subheadingTeacher")
      : t("subheadingStudent")
    : null;

  const altRoleHref = signupRole === "TEACHER" ? "/auth?role=student" : "/auth?role=teacher";
  const altRoleLabel =
    signupRole === "TEACHER" ? t("switchToLearn") : t("switchToTeach");

  return (
    <div className="bg-[#E9E9EA] pb-8 md:pb-12">
      <div className="rounded-b-semifull flex w-full flex-col items-center bg-[#27335d] px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-6 flex w-full max-w-md justify-end">
          <LanguageSwitcher tone="dark" />
        </div>
        <div className="mx-auto w-full max-w-md text-center">
          <h1
            data-testid="authHeading"
            className="text-3xl font-semibold text-white sm:text-4xl md:text-5xl"
          >
            {heading}
          </h1>
          {subheading && (
            <p className="mt-4 text-base text-white/80">{subheading}</p>
          )}
        </div>

        <div className="mt-10 w-full max-w-md" data-testid="authPage">
          {errorCopy && (
            <div
              role="alert"
              data-testid="authErrorBanner"
              data-error-code={errorParam ?? ""}
              className="mb-6 rounded-xl border-2 border-[#B53135]/40 bg-[#B53135]/10 p-4 text-left text-white"
            >
              <p
                data-testid="authErrorBannerTitle"
                className="text-base font-semibold"
              >
                {errorCopy.title}
              </p>
              <p
                data-testid="authErrorBannerMessage"
                className="mt-1 text-sm text-white/80"
              >
                {errorCopy.body}
              </p>
            </div>
          )}
          {providersLoading && (
            <div
              data-testid="authProvidersLoading"
              className="py-8 text-center text-sm text-white/70"
            >
              {t("loading")}
            </div>
          )}

          {noProvidersEnabled && (
            <div
              data-testid="authDisabledMessage"
              className="rounded-xl border-2 border-white/20 bg-white/5 p-5 text-center text-sm text-white"
            >
              {t("signInDisabled")}
            </div>
          )}

          {showGoogle && (
            <button
              type="button"
              data-testid="authGoogleButton"
              onClick={() => handleOAuthClick("google")}
              className="flex w-full cursor-pointer items-center justify-center gap-3 rounded-xl border-2 border-gray-200 bg-white px-6 py-4 text-base font-semibold text-gray-700 shadow-sm transition-all hover:bg-gray-50 hover:shadow-md"
            >
              <svg className="h-6 w-6" viewBox="0 0 48 48">
                <path
                  fill="#EA4335"
                  d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                />
                <path
                  fill="#4285F4"
                  d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                />
                <path
                  fill="#FBBC05"
                  d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.01 24.01 0 0 0 0 21.56l7.98-6.19z"
                />
                <path
                  fill="#34A853"
                  d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                />
              </svg>
              {t("continueWithGoogle")}
            </button>
          )}

          {showApple && (
            <button
              type="button"
              data-testid="authAppleButton"
              onClick={() => handleOAuthClick("apple")}
              className="mt-3 flex w-full cursor-pointer items-center justify-center gap-3 rounded-xl border-2 border-black bg-black px-6 py-4 text-base font-semibold text-white shadow-sm transition-all hover:bg-gray-900"
            >
              {t("continueWithApple")}
            </button>
          )}

          {showMockOauth && (
            <button
              type="button"
              data-testid="authMockOauthButton"
              onClick={() => handleOAuthClick("mock")}
              className="mt-3 flex w-full cursor-pointer items-center justify-center gap-3 rounded-xl border-2 border-dashed border-amber-300/70 bg-amber-100/10 px-6 py-4 text-base font-semibold text-amber-100 shadow-sm transition-all hover:bg-amber-100/20"
            >
              <span aria-hidden>🧪</span> {t("useMockLogin")}
            </button>
          )}

          {signupRole && (
            <div className="mt-6 text-center text-sm text-white/70">
              <Link
                href={altRoleHref}
                data-testid="authSwitchRoleLink"
                className="text-[#4887F5] hover:text-[#6D9FF7]"
              >
                {altRoleLabel}
              </Link>
            </div>
          )}
        </div>

        <div className="mt-10">
          <Image
            src="/images/svg/marhabaregister2.svg"
            alt="Marhaba"
            width={200}
            height={100}
            className="h-auto w-36"
          />
        </div>
      </div>
    </div>
  );
}
